/** ***********
*
* <dialog-generate-events.component.ts v1.0>
*
* Copyright (c) 2024 AIRBUS OPERATIONS
*
* All rights reserved.
**************/

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subscription, combineLatest, iif } from 'rxjs';
import { SnackbarComponent } from 'src/app/components/shared/snackbar/snackbar';
import { Asset } from 'src/app/models/asset';
import { Zone } from 'src/app/models/zone';
import { ErrorTranslationService } from 'src/app/services/error-translation.service';
import { selectAssetState$, selectAssetsArray$ } from 'src/app/store/selectors/asset.selectors';
import { selectZones$, selectZonesLoaded$ } from 'src/app/store/selectors/zone.selector';
import { DevToolsService } from '../../dev-tools.service';
import { MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { selectTrackerByAssetsIds$, selectTrackerState$, selectTrackers$ } from 'src/app/store/selectors/tracker.selectors';
import { EventDev, EventState, EventType, AswarmStatus } from 'src/app/models/dev-tools.model';
import { SignalSourceProviderValues } from 'src/app/models/signal-source';

@Component({
    selector: 'app-dialog-generate-events',
    templateUrl: './dialog-generate-events.component.html',
    styleUrls: ['./dialog-generate-events.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DialogGenerateEventsComponent implements OnInit {

    public eventForm: FormGroup<{
        supplier: FormControl<SignalSourceProviderValues>;
        asset: FormControl<Asset['id']>;
        type: FormControl<EventType>;
        status: FormControl<AswarmStatus>;
        data: FormControl<string>;
        latitude: FormControl<number>;
        longitude: FormControl<number>;
        state: FormControl<EventState>;
        zone: FormControl<Zone['id']>;
        accuracy: FormControl<number>;
    }> = this._fb.group({
        supplier: this._fb.control(null, [Validators.required]),
        asset: this._fb.control(null, [Validators.required]),
        type: this._fb.control(null, [Validators.required]),
        status: this._fb.control(null),
        data: this._fb.control(null),
        latitude: this._fb.control(null, [Validators.min(-90), Validators.max(90)]),
        longitude: this._fb.control(null, [Validators.min(-180), Validators.max(180)]),
        state: this._fb.control(null),
        zone: this._fb.control(null),
        accuracy: this._fb.control(null, [Validators.min(0)]),
    });
    public searchForm: FormGroup<{
        supplier: FormControl<string>;
        asset: FormControl<string>;
        type: FormControl<string>;
        status: FormControl<string>;
        zone: FormControl<string>;
        state: FormControl<string>;
    }> = this._fb.group({
        supplier: this._fb.control(''),
        asset: this._fb.control(''),
        type: this._fb.control(''),
        status: this._fb.control(''),
        zone: this._fb.control(''),
        state: this._fb.control(''),
    });
    public readonly SignalSource = SignalSourceProviderValues;
    public readonly EventType = EventType;
    public readonly Status = AswarmStatus;
    public readonly State = EventState;

    // Loading states
    public loadings = {
        assets$: this.eventForm.controls.supplier.valueChanges.pipe(
            startWith(null),
            distinctUntilChanged(),
            switchMap(
                selectedSupplier => iif(
                    () => !!selectedSupplier,
                    combineLatest([
                        this.store.select(selectAssetState$),
                        this.store.select(selectTrackerState$),
                    ]).pipe(
                        map(([assetState, trackerState]) => !assetState.loaded || !trackerState.loaded),
                    ),
                    this.store.select(selectAssetState$).pipe(
                        map((assetState) => !assetState.loaded),
                    ),
                )
            ),
            tap(() => this._cd.markForCheck()),
        ),
        zones$: this.store.select(selectZonesLoaded$).pipe(
            map((loaded) => !loaded),
            tap(() => this._cd.markForCheck()),
        ),
        submit: false,
    };

    // List of assets
    public assets$: Observable<Asset[]> = this.eventForm.controls.supplier.valueChanges.pipe(
        startWith(null),
        distinctUntilChanged(),
        switchMap(
            selectedSupplier => iif(
                () => !!selectedSupplier,
                combineLatest([
                    this.store.select(selectAssetsArray$),
                    this.store.select(selectTrackers$),
                ]).pipe(
                    map(
                        ([assets, { trackers }]) => assets.filter(asset => {
                            if (!asset.trackerId) {
                                return false;
                            }
                            const tracker = trackers[asset.trackerId];
                            return tracker?.provider === selectedSupplier;
                        })
                    )
                ),
                this.store.select(selectAssetsArray$),
            )
        ),
        tap(() => this._cd.markForCheck()),
    );

    // List of zones
    public zones$: Observable<Zone[]> = this.store.select(selectZones$).pipe(
        tap(() => this._cd.markForCheck()),
    );

    private _subscription: Subscription = new Subscription();

    constructor(
        private readonly store: Store,
        private readonly _fb: FormBuilder,
        private readonly _cd: ChangeDetectorRef,
        private _devToolsService: DevToolsService,
        private _snackBar: SnackbarComponent,
        private _errorTranslationService: ErrorTranslationService,
        public dialogRef: MatDialogRef<DialogGenerateEventsComponent>
    ) { }

    ngOnInit(): void {
        this._setRequiredFieldOnEventType();
        this._watchFormChanges();
    }

    private _setRequiredFieldOnEventType() {
        this._subscription.add(
            this.eventForm.controls.supplier.valueChanges.pipe(
                distinctUntilChanged(),
            ).subscribe(
                supplier => {
                    const { status } = this.eventForm.controls;
                    if (supplier === this.SignalSource.aswarm) {
                        status.addValidators(Validators.required);
                    } else {
                        status.removeValidators(Validators.required);
                    }
                }
            )
        );
        this._subscription.add(
            this.eventForm.controls.type.valueChanges.pipe(
                distinctUntilChanged(),
            ).subscribe(
                eventType => {
                    this.eventForm.controls.data.removeValidators(Validators.required);
                    this.eventForm.controls.latitude.removeValidators(Validators.required);
                    this.eventForm.controls.longitude.removeValidators(Validators.required);
                    this.eventForm.controls.state.removeValidators(Validators.required);
                    this.eventForm.controls.zone.removeValidators(Validators.required);
                    this.eventForm.controls.accuracy.removeValidators(Validators.required);
                    switch (eventType) {
                        case EventType.data:
                            this.eventForm.controls.data.addValidators(Validators.required);
                            break;
                        case EventType.location:
                            this.eventForm.controls.latitude.addValidators(Validators.required);
                            this.eventForm.controls.longitude.addValidators(Validators.required);
                            break;
                        case EventType.state:
                            this.eventForm.controls.state.addValidators(Validators.required);
                            break;
                        case EventType.zone:
                            this.eventForm.controls.zone.addValidators(Validators.required);
                            this.eventForm.controls.state.addValidators(Validators.required);
                            this.eventForm.controls.accuracy.addValidators(Validators.required);
                            break;
                    }
                }
            )
        );
    }

    private _watchFormChanges() {
        this._subscription.add(
            this.eventForm.controls.supplier.valueChanges.pipe(
                distinctUntilChanged(),
                switchMap(
                    () => iif(
                        () => !this.eventForm.value.asset,
                        EMPTY,
                        this.store.select(selectTrackerByAssetsIds$([this.eventForm.value.asset]))
                    )
                )
            ).subscribe(
                ([tracker]) => {
                    if (tracker?.provider !== this.eventForm.value.supplier) {
                        this.eventForm.patchValue({
                            asset: null,
                        });
                    }
                }
            )
        );
    }

    public trackById(item) {
        return item.id;
    }

    /** Preparing Event Data */
    private generateEventData(formData: typeof this.eventForm.value): EventDev {
        const commonData: Omit<EventDev, "type" | "item"> = {
            assetId: formData.asset,
            provider: formData.supplier,
            status: AswarmStatus[formData.status],
        };
        switch (formData.type) {
            case EventType.location:
                return {
                    ...commonData,
                    type: EventType.location,
                    item: {
                        lat: formData.latitude,
                        lng: formData.longitude,
                    },
                };
            case EventType.data:
                return {
                    ...commonData,
                    type: EventType.data,
                    item: {
                        data: formData.data,
                    },
                };
            case EventType.state:
                return {
                    ...commonData,
                    type: EventType.state,
                    item: {
                        state: formData.state,
                    },
                };
            case EventType.zone:
                return {
                    ...commonData,
                    type: EventType.zone,
                    item: {
                        zone: formData.zone,
                        accuracy: formData.accuracy,
                        state: formData.state,
                    },
                };
            default:
                throw new RangeError(
                    `The type ${formData.type} is not managed, it must be in the range [${Object.values(EventType)}]`
                );
        }
    }

    /** Generate event API call */
    public async generateEvent() {
        const formData = this.eventForm.value;
        const dataToPost = await this.generateEventData(formData);
        this.loadings.submit = true;
        this._subscription.add(this._devToolsService.postEvent(dataToPost).subscribe({
            next: () => {
                this._snackBar.open('Event Generated', 'green-snackbar', 5000);
                this.dialogRef.close();
            },
            error: (err) => {
                this._errorTranslationService.handleError(err, 'BANNER_FAIL_INTERNAL_CREATE');
                this.loadings.submit = false;
            },
            complete: () => {
                this.loadings.submit = false;

            }
        }));
    }
}
